(() => {
  'use strict';

  const SETTINGS = {
    giftVariantId: 53801985376570,
    thresholdCents: 7500,
    giftProperty: '_automatic_free_gift',
    giftPropertyValue: 'true',
    debug: true
  };

  let syncing = false;
  let timer = null;
  let internalRequest = false;

  const log = (...args) => {
    if (SETTINGS.debug) {
      console.log('[Free Gift]', ...args);
    }
  };

  const rootUrl = window.Shopify?.routes?.root || '/';

  const cartEndpoint = (endpoint) => {
    return `${rootUrl}cart/${endpoint}`;
  };

  const getCart = async () => {
    const response = await fetch(cartEndpoint('js'), {
      headers: {
        Accept: 'application/json'
      },
      credentials: 'same-origin'
    });

    if (!response.ok) {
      throw new Error(`Unable to load cart: ${response.status}`);
    }

    return response.json();
  };

  const isAutomaticGift = (item) => {
    return (
      Number(item.variant_id) === SETTINGS.giftVariantId &&
      item.properties?.[SETTINGS.giftProperty] ===
        SETTINGS.giftPropertyValue
    );
  };

  const qualifyingSubtotal = (cart) => {
    return cart.items.reduce((total, item) => {
      if (isAutomaticGift(item)) {
        return total;
      }

      return total + Number(item.final_line_price || 0);
    }, 0);
  };

  const addGift = async () => {
    internalRequest = true;

    try {
      const response = await fetch(cartEndpoint('add.js'), {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({
          items: [
            {
              id: SETTINGS.giftVariantId,
              quantity: 1,
              properties: {
                [SETTINGS.giftProperty]:
                  SETTINGS.giftPropertyValue
              }
            }
          ]
        })
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));

        throw new Error(
          error.description ||
            error.message ||
            `Unable to add gift: ${response.status}`
        );
      }

      log('Gift added');
    } finally {
      internalRequest = false;
    }
  };

  const removeGift = async (giftItem) => {
    internalRequest = true;

    try {
      const response = await fetch(cartEndpoint('change.js'), {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({
          id: giftItem.key,
          quantity: 0
        })
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));

        throw new Error(
          error.description ||
            error.message ||
            `Unable to remove gift: ${response.status}`
        );
      }

      log('Gift removed');
    } finally {
      internalRequest = false;
    }
  };

  const notifyTheme = async () => {
    const cart = await getCart();

    const events = [
      'cart:updated',
      'cart:update',
      'cart:refresh',
      'cart:change',
      'cart:changed'
    ];

    events.forEach((eventName) => {
      document.dispatchEvent(
        new CustomEvent(eventName, {
          bubbles: true,
          detail: {
            cart,
            source: 'automatic-free-gift'
          }
        })
      );

      document.documentElement.dispatchEvent(
        new CustomEvent(eventName, {
          bubbles: true,
          detail: {
            cart,
            source: 'automatic-free-gift'
          }
        })
      );
    });

    window.dispatchEvent(
      new CustomEvent('cart:updated', {
        detail: {
          cart,
          source: 'automatic-free-gift'
        }
      })
    );
  };

  const syncGift = async () => {
    if (syncing) return;

    syncing = true;

    try {
      const cart = await getCart();
      const subtotal = qualifyingSubtotal(cart);
      const giftItems = cart.items.filter(isAutomaticGift);
      const qualifies = subtotal >= SETTINGS.thresholdCents;

      log({
        subtotal,
        threshold: SETTINGS.thresholdCents,
        qualifies,
        giftCount: giftItems.length
      });

      let changed = false;

      if (qualifies && giftItems.length === 0) {
        await addGift();
        changed = true;
      }

      if (!qualifies && giftItems.length > 0) {
        for (const giftItem of giftItems) {
          await removeGift(giftItem);
        }

        changed = true;
      }

      if (qualifies && giftItems.length > 1) {
        for (const duplicateGift of giftItems.slice(1)) {
          await removeGift(duplicateGift);
        }

        changed = true;
      }

      if (changed) {
        await notifyTheme();
      }
    } catch (error) {
      console.error('[Free Gift]', error);
    } finally {
      syncing = false;
    }
  };

  const scheduleSync = () => {
    clearTimeout(timer);

    timer = setTimeout(() => {
      syncGift();
    }, 300);
  };

  const originalFetch = window.fetch.bind(window);

  window.fetch = async (...args) => {
    const response = await originalFetch(...args);

    try {
      const requestUrl =
        typeof args[0] === 'string'
          ? args[0]
          : args[0]?.url || '';

      const isCartChange =
        requestUrl.includes('/cart/add') ||
        requestUrl.includes('/cart/change') ||
        requestUrl.includes('/cart/update') ||
        requestUrl.includes('/cart/clear');

      if (isCartChange && !internalRequest) {
        scheduleSync();
      }
    } catch (error) {
      console.warn('[Free Gift] Could not inspect request', error);
    }

    return response;
  };

  document.addEventListener('change', (event) => {
    if (
      event.target.matches(
        'input[name="updates[]"], input[name="quantity"], select[name="quantity"]'
      )
    ) {
      setTimeout(scheduleSync, 400);
    }
  });

  document.addEventListener('click', (event) => {
    const cartButton = event.target.closest(
      [
        '[name="add"]',
        '[data-cart-add]',
        '[data-cart-remove]',
        '[data-quantity-plus]',
        '[data-quantity-minus]',
        '.quantity__button',
        '.cart-remove-button',
        'cart-remove-button'
      ].join(',')
    );

    if (cartButton) {
      setTimeout(scheduleSync, 400);
    }
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scheduleSync);
  } else {
    scheduleSync();
  }
})();